// Reference material for work cards, edited where it belongs: SOPs on an operation, checklist
// lines on an operation or on a catalog item. A card pulls these together on its own; nothing
// is written per card.

import { useRef } from 'react';
import { deleteRow, insertRow, updateRow, useRows, type Row } from '../lib/api';
import { useSession } from '../lib/session';
import { Btn, Edit, Empty, Info, Tag } from '../ui/kit';
import { uploadFile, useSignedUrl } from './job/Files';

export const sopCode = (id: string) => `ZG-S-${id.slice(0, 8).toUpperCase()}`;

/** Opens an SOP: its link, or its uploaded file. */
export function SopLink({ sop, children }: { sop: Row; children?: React.ReactNode }) {
  const file = useRows('file', { eq: { id: sop.file_id }, enabled: !!sop.file_id }).rows[0];
  const signed = useSignedUrl(file?.storage_path);
  const href = sop.url || signed;
  if (!href) return <span title="No link or file yet">{children ?? sop.title}</span>;
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children ?? sop.title}
    </a>
  );
}

export function SopList({ operationId, canEdit }: { operationId: string; canEdit: boolean }) {
  const { user } = useSession();
  const sops = useRows('sop', { eq: { operation_id: operationId }, order: ['sort', 'created_at'] });
  const input = useRef<HTMLInputElement>(null);
  const target = useRef<string | null>(null);

  const add = async () => {
    const row = await insertRow('sop', { operation_id: operationId, title: 'New SOP', sort: sops.rows.length + 1 });
    await insertRow('label', { code: sopCode(row.id), kind: 'sop', target_id: row.id });
  };
  const attach = async (list: FileList | null) => {
    const picked = list?.[0];
    if (!picked || !target.current) return;
    const file = await uploadFile(picked, 'sop', target.current, user?.id ?? null);
    await updateRow('sop', target.current, { file_id: file.id });
    if (input.current) input.current.value = '';
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div className="lb">
        <span>
          SOPs · {sops.rows.length}
          <Info>A standard operating procedure for this operation. Every work card with this operation shows it beside the step, and the printed traveler carries a code that opens it.</Info>
        </span>
        {canEdit && (
          <Btn small onClick={() => void add()}>
            + SOP
          </Btn>
        )}
      </div>
      <input ref={input} type="file" accept="application/pdf,image/*" className="hide" onChange={(e) => void attach(e.target.files)} />
      {sops.rows.length === 0 && <Empty>No SOP for this operation yet.</Empty>}
      {sops.rows.map((x) => (
        <div key={x.id} className="row mid" style={{ gap: 6, padding: '3px 0' }}>
          <span className="grow">
            <Edit disabled={!canEdit} value={x.title} onSave={(v) => updateRow('sop', x.id, { title: v || x.title })} />
          </span>
          <span className="grow">
            <Edit disabled={!canEdit} value={x.url ?? ''} placeholder="Link (optional)" onSave={(v) => updateRow('sop', x.id, { url: v || null })} />
          </span>
          {x.file_id ? <SopLink sop={{ ...x, url: null }}>file</SopLink> : null}
          {!x.verified && <Tag kind="un">unverified</Tag>}
          {canEdit && (
            <>
              <Btn small onClick={() => { target.current = x.id; input.current?.click(); }}>
                {x.file_id ? 'Replace file' : 'Upload file'}
              </Btn>
              <Btn small onClick={() => window.confirm(`Remove the SOP "${x.title}"?`) && void deleteRow('sop', x.id)}>
                Remove
              </Btn>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export function ChecklistLines({ operationId, catalogItemId, canEdit }: { operationId?: string; catalogItemId?: string; canEdit: boolean }) {
  const eq = operationId ? { operation_id: operationId } : { catalog_item_id: catalogItemId };
  const lines = useRows('checklist_line', { eq, order: ['sort', 'created_at'], enabled: !!(operationId || catalogItemId) });
  return (
    <div style={{ marginTop: 14 }}>
      <div className="lb">
        <span>
          Checklist lines · {lines.rows.length}
          <Info>
            A line every work card shows when it {operationId ? 'has this operation' : 'carries this item'}. "Must tick" means the card cannot close until someone ticks it. "Only when" is optional: a formula over the item's fields
            (for example side_type_left = 'Applied Side'), so the line shows only for the items where it holds.
          </Info>
        </span>
        {canEdit && (
          <Btn small onClick={() => void insertRow('checklist_line', { ...eq, text: 'New checklist line', sort: lines.rows.length + 1 })}>
            + Line
          </Btn>
        )}
      </div>
      {lines.rows.length === 0 && <Empty>No checklist lines here yet.</Empty>}
      {lines.rows.map((x) => (
        <div key={x.id} className="row mid" style={{ gap: 6, padding: '3px 0' }}>
          <span className="grow" style={{ flex: 2 }}>
            <Edit disabled={!canEdit} value={x.text} onSave={(v) => updateRow('checklist_line', x.id, { text: v || x.text })} />
          </span>
          <span className="grow">
            <Edit disabled={!canEdit} value={x.show_when_expr ?? ''} placeholder="Only when… (optional)" onSave={(v) => updateRow('checklist_line', x.id, { show_when_expr: v || null })} />
          </span>
          <label className="check nowrap">
            <input type="checkbox" disabled={!canEdit} checked={!!x.required} onChange={(e) => void updateRow('checklist_line', x.id, { required: e.target.checked })} />
            Must tick
          </label>
          {!x.verified && <Tag kind="un">unverified</Tag>}
          {canEdit && (
            <Btn small onClick={() => window.confirm('Remove this checklist line?') && void deleteRow('checklist_line', x.id)}>
              Remove
            </Btn>
          )}
        </div>
      ))}
    </div>
  );
}
